import { useRef } from "react"

import type { CONVERSATION_SEARCH_TYPE } from "../types"
import { useConversationsSelectionResetOnFilterChange } from "./useConversationsSelectionResetOnFilterChange"
import { useConversationsVisualizerPhantomSelectionClear } from "./useConversationsVisualizerPhantomSelectionClear"
import { useConversationsVisualizerSelection } from "./useConversationsVisualizerSelection"

interface UseConversationsVisualizerFilterCoordinationParams {
  agentIds: string[]
  clearAllFilters: () => void
  clusterFilterIds: string[] | null
  conversationCount: number
  dateRange: unknown
  hasActiveFilters: boolean
  isFetching: boolean
  metadataFilters: unknown
  metricIds: string[]
  metricValueFilters: unknown
  searchQuery: string
  searchType: (typeof CONVERSATION_SEARCH_TYPE)[keyof typeof CONVERSATION_SEARCH_TYPE]
  selectedKeys: Set<string>
  setActiveItemId: (itemId: string) => void
  setClusterFilterIds: (ids: string[] | null) => void
  setSearchQuery: (query: string) => void
  setSearchType: (
    searchType: (typeof CONVERSATION_SEARCH_TYPE)[keyof typeof CONVERSATION_SEARCH_TYPE],
  ) => void
  setSelectedKeys: (keys: Set<string>) => void
  tableHasInitiallyLoaded: boolean
  tagFilters: unknown
  projectIds: string[]
}

/**
 * Composes the hooks that are only ever used together in support of the visualizer's
 * click-to-filter behavior: useConversationsVisualizerSelection (the click handlers
 * themselves), useConversationsSelectionResetOnFilterChange (which must skip the one
 * filter change those handlers make on purpose), and
 * useConversationsVisualizerPhantomSelectionClear (which undoes a conversation-click
 * selection if the exact-match filter it applied turns out to match nothing). Splitting
 * these further would just spread one coordination concern across more call sites.
 */
export function useConversationsVisualizerFilterCoordination({
  agentIds,
  clearAllFilters,
  clusterFilterIds,
  conversationCount,
  dateRange,
  hasActiveFilters,
  isFetching,
  metadataFilters,
  metricIds,
  metricValueFilters,
  searchQuery,
  searchType,
  selectedKeys,
  setActiveItemId,
  setClusterFilterIds,
  setSearchQuery,
  setSearchType,
  setSelectedKeys,
  tableHasInitiallyLoaded,
  tagFilters,
  projectIds,
}: UseConversationsVisualizerFilterCoordinationParams) {
  const tableScrollContainerRef = useRef<HTMLDivElement>(null)

  const {
    consumeSkipNextSelectionReset,
    handleVisualizerClusterFilter,
    handleVisualizerConversationClick,
  } = useConversationsVisualizerSelection({
    clearAllFilters,
    clusterFilterIds,
    hasActiveFilters,
    searchQuery,
    searchType,
    setActiveItemId,
    setClusterFilterIds,
    setSearchQuery,
    setSearchType,
    setSelectedKeys,
    tableScrollContainerRef,
  })

  useConversationsSelectionResetOnFilterChange({
    agentIds,
    clusterFilterIds,
    consumeSkipNextSelectionReset,
    dateRange,
    metadataFilters,
    metricIds,
    metricValueFilters,
    searchQuery,
    searchType,
    setSelectedKeys,
    tagFilters,
    projectIds,
  })

  useConversationsVisualizerPhantomSelectionClear({
    clusterFilterIds,
    conversationCount,
    isFetching,
    searchType,
    selectedKeys,
    setSelectedKeys,
    tableHasInitiallyLoaded,
  })

  return {
    handleVisualizerClusterFilter,
    handleVisualizerConversationClick,
    tableScrollContainerRef,
  }
}
